import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer, Loader2, Save, RefreshCw, Edit3 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { generatePdfBlob } from "@/lib/pdfExport";

const brl = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const AdminProposta = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const embed = searchParams.get("embed") === "1";
  const autopdf = searchParams.get("autopdf") === "1";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    description: "",
    technical_argument: "",
    next_steps: "",
  });
  const pdfRootRef = useRef(null);
  const sentRef = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/diagnostics/${id}/proposal`);
      setData(r.data);
      setForm({
        description: r.data.proposal.description,
        technical_argument: r.data.proposal.technical_argument,
        next_steps: r.data.proposal.next_steps,
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (!autopdf || loading || !data || sentRef.current) return;
    sentRef.current = true;
    setTimeout(async () => {
      try {
        const blob = await generatePdfBlob(pdfRootRef.current, "proposta.pdf");
        window.parent?.postMessage(
          { type: "pdf-ready", kind: "proposta", blob },
          window.location.origin
        );
      } catch (err) {
        window.parent?.postMessage(
          { type: "pdf-error", kind: "proposta", error: String(err?.message || err) },
          window.location.origin
        );
      }
    }, 700);
  }, [autopdf, loading, data]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/diagnostics/${id}/proposal`, form);
      toast.success("Proposta salva.");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm("Restaurar textos padrão? Sua edição será perdida.")) return;
    setSaving(true);
    try {
      await api.put(`/diagnostics/${id}/proposal`, {
        description: "",
        technical_argument: "",
        next_steps: "",
      });
      // Re-load — but the backend stores empty strings; we want true reset. Send actual reset.
      // Simpler: store strings; backend will use stored if non-empty, falls back if empty handled by frontend
      toast.success("Textos restaurados.");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#6B8DB3]">
        <Loader2 className="animate-spin mr-2" /> Carregando proposta...
      </div>
    );
  }
  if (!data) return null;

  const { diagnostic: d, proposal } = data;
  const sc = d.scoring || {};
  const cls = sc.classification || {};
  const top3 = proposal.critical_sections || [];

  return (
    <div className={`min-h-screen ${embed ? "bg-white text-[#1C1F26]" : "bg-[#0e1015] text-white"}`}>
      {/* Toolbar */}
      {!embed && (
      <div className="print-hide sticky top-0 z-20 bg-[#050608]/95 border-b border-[#1C1F26] backdrop-blur">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3 flex-wrap">
          <Button
            variant="ghost"
            onClick={() => navigate(`/admin/diagnosticos/${id}`)}
            className="text-[#9aa3ad] hover:text-white hover:bg-[#1C1F26]"
          >
            <ArrowLeft size={16} className="mr-2" /> Voltar
          </Button>
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setEditing(false);
                    setForm({
                      description: proposal.description,
                      technical_argument: proposal.technical_argument,
                      next_steps: proposal.next_steps,
                    });
                  }}
                  className="text-[#9aa3ad] hover:text-white hover:bg-[#1C1F26]"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={save}
                  disabled={saving}
                  data-testid="save-proposal-btn"
                  className="bg-[#22B673] hover:bg-[#1a9b60] text-white"
                >
                  {saving ? <Loader2 className="animate-spin" size={16} /> : (
                    <><Save size={14} className="mr-2" /> Salvar</>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={resetToDefault}
                  disabled={saving}
                  className="border-[#3A3F47] text-white hover:bg-[#1C1F26]"
                >
                  <RefreshCw size={14} className="mr-2" /> Restaurar padrão
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setEditing(true)}
                  data-testid="edit-proposal-btn"
                  className="border-[#3A3F47] text-white hover:bg-[#1C1F26]"
                >
                  <Edit3 size={14} className="mr-2" /> Editar textos
                </Button>
                <Button
                  onClick={() => window.print()}
                  data-testid="export-pdf-btn"
                  className="bg-[#B9262B] hover:bg-[#9a1e22] text-white"
                >
                  <Printer size={16} className="mr-2" /> Exportar PDF
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Conteúdo (1 página) */}
      <main
        ref={pdfRootRef}
        data-pdf-root="proposta"
        className={`print-page ${embed ? "max-w-[210mm] mx-auto bg-white text-[#1C1F26]" : "max-w-[210mm] mx-auto bg-white text-[#1C1F26] my-6 shadow-2xl"}`}
      >
        <div className="p-9 print:p-0">
          {/* Header */}
          <div className="flex items-start justify-between border-b-2 border-[#B9262B] pb-4 mb-5">
            <div>
              <img src={LOGO_URL} alt="UniGR" style={{ height: 48 }} className="object-contain" />
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#6B8DB3] mt-1.5">
                Conhecimento que transforma
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#3A3F47]">
                Proposta Comercial
              </div>
              <div className="text-lg font-bold text-[#0A2342] leading-tight">
                Diagnóstico de Gestão de Frota
              </div>
              <div className="text-[10px] text-[#4a5360] mt-0.5">
                {new Date().toLocaleDateString("pt-BR")}
              </div>
            </div>
          </div>

          {/* Empresa + KPIs principais */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-bold text-[#0A2342] leading-tight">{d.company}</h1>
              <div className="text-[11px] text-[#4a5360]">
                CNPJ {d.cnpj || "—"} · Consultor: {d.consultant || "—"}
                {d.city_uf ? ` · ${d.city_uf}` : ""}
              </div>
            </div>
            <div className="flex gap-2">
              <PropCard label="Maturidade" value={sc.level_short} color={cls.color} />
              <PropCard
                label="Score"
                value={`${Math.round(sc.total)}%`}
                color={cls.color}
              />
              <PropCard
                label="Economia mensal"
                value={`${brl(sc.economy?.min_value)} — ${brl(sc.economy?.max_value)}`}
                color="#22B673"
                small
              />
            </div>
          </div>

          {/* Descrição da proposta */}
          <ProposalBlock title="Descrição da Proposta">
            {editing ? (
              <Textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-[#f6f7f9] border-[#cfd4dc] text-[#1C1F26]"
              />
            ) : (
              <p className="text-[11.5px] leading-relaxed text-[#1C1F26]">
                {proposal.description}
              </p>
            )}
          </ProposalBlock>

          {/* Top 3 sessões críticas */}
          <ProposalBlock title="Dimensões Mais Críticas & Produtos Recomendados">
            <div className="space-y-2">
              {top3.map((s, i) => (
                <div
                  key={i}
                  className="border border-[#cfd4dc] rounded p-2.5"
                  style={{ borderLeft: `3px solid ${s.color}` }}
                >
                  <div className="flex justify-between items-baseline mb-1">
                    <div className="text-[11px] font-bold text-[#0A2342] uppercase tracking-wider leading-tight">
                      {s.name}
                    </div>
                    <div className="text-[11px] font-mono font-bold" style={{ color: s.color }}>
                      {Math.round(s.score)}%
                    </div>
                  </div>
                  <div className="text-[9px] text-[#4a5360] uppercase tracking-wider mb-1">
                    {s.level} · Prioridade {s.priority} · Prazo: {s.timeline}
                  </div>
                  {s.recommended_products && s.recommended_products.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      {s.recommended_products.map((p) => (
                        <span
                          key={p.id}
                          className="inline-flex items-center gap-1 text-[9.5px] px-2 py-0.5 rounded-full bg-[#0A2342] text-white font-semibold"
                        >
                          {p.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-[10px] text-[#4a5360] italic">
                      {s.product_recommendation}
                    </div>
                  )}
                </div>
              ))}
              {top3.length === 0 && (
                <div className="text-[11px] text-[#4a5360]">
                  Não foi possível determinar as dimensões críticas.
                </div>
              )}
            </div>
          </ProposalBlock>

          {/* Produtos UniGR recomendados (overall) */}
          {proposal.recommended_products && proposal.recommended_products.length > 0 && (
            <ProposalBlock title="Módulos UniGR Recomendados">
              <div className="grid grid-cols-2 gap-2">
                {proposal.recommended_products.map((p) => (
                  <div
                    key={p.id}
                    className="border border-[#cfd4dc] rounded p-2 print-card"
                    style={{ borderLeft: `3px solid #B9262B` }}
                  >
                    <div className="text-[10.5px] font-bold text-[#0A2342] leading-tight mb-0.5">
                      {p.name}
                    </div>
                    <div className="text-[9px] text-[#4a5360] mb-1">
                      {p.timeline} · {p.pricing_model}
                    </div>
                    <div className="text-[10px] text-[#1C1F26] leading-snug">
                      {p.objective}
                    </div>
                  </div>
                ))}
              </div>
            </ProposalBlock>
          )}

          {/* Argumentação técnica */}
          <ProposalBlock title="Argumentação Técnica">
            {editing ? (
              <Textarea
                rows={3}
                value={form.technical_argument}
                onChange={(e) => setForm({ ...form, technical_argument: e.target.value })}
                className="bg-[#f6f7f9] border-[#cfd4dc] text-[#1C1F26]"
              />
            ) : (
              <p className="text-[11.5px] leading-relaxed text-[#1C1F26]">
                {proposal.technical_argument}
              </p>
            )}
          </ProposalBlock>

          {/* Próximos passos */}
          <ProposalBlock title="Próximos Passos Sugeridos">
            {editing ? (
              <Textarea
                rows={4}
                value={form.next_steps}
                onChange={(e) => setForm({ ...form, next_steps: e.target.value })}
                className="bg-[#f6f7f9] border-[#cfd4dc] text-[#1C1F26]"
              />
            ) : (
              <p className="text-[11.5px] leading-relaxed text-[#1C1F26] whitespace-pre-wrap">
                {proposal.next_steps}
              </p>
            )}
          </ProposalBlock>

          {/* Footer */}
          <div className="mt-5 pt-3 border-t border-[#cfd4dc] flex items-center justify-between">
            <div className="text-[9px] text-[#4a5360]">
              UniGR · Transporte · Logística · Segurança · Inteligência de Dados
            </div>
            <div className="text-[9px] text-[#4a5360] font-mono">
              Protocolo {d.id?.slice(0, 8)}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const PropCard = ({ label, value, color, small }) => (
  <div
    className="border border-[#cfd4dc] rounded px-3 py-1.5 print-card text-center"
    style={{ borderTop: `2px solid ${color}` }}
  >
    <div className="text-[8px] uppercase tracking-wider text-[#4a5360]">
      {label}
    </div>
    <div
      className={small ? "text-[10px] font-bold leading-tight" : "text-base font-bold leading-tight"}
      style={{ color }}
    >
      {value}
    </div>
  </div>
);

const ProposalBlock = ({ title, children }) => (
  <div className="mb-3">
    <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#B9262B] mb-1.5">
      {title}
    </h3>
    {children}
  </div>
);

export default AdminProposta;
